// Download Logging Service for Phase 1C
// Tracks download events, analytics, and monitors failure rates

import { supabase } from "@shared/core/services/SupabaseService";

interface DownloadEvent {
  downloadId: string;
  token: string;
  eventType: "started" | "progress" | "completed" | "failed" | "cancelled";
  timestamp: Date;
  metadata: {
    productId?: string;
    websiteId?: string;
    fileType?: "zip" | "audio" | "other";
    fileSize?: number;
    contentType?: string;
    bytesTransferred?: number;
    totalBytes?: number;
    duration?: number;
    errorCode?: string;
    errorMessage?: string;
    userAgent?: string;
    ipAddress?: string;
    resumeAttempt?: boolean;
    resumePosition?: number;
  };
}

interface DownloadAnalytics {
  totalDownloads: number;
  completedDownloads: number;
  failedDownloads: number;
  totalBytesTransferred: number;
  averageDownloadTime: number;
  successRate: number;
  failuresByError: Record<string, number>;
  downloadsByFileType: Record<string, number>;
  resumeAttempts: number;
}

interface PerformanceMetrics {
  averageSpeed: number; // KB/s
  p95DownloadTime: number; // ms
  p99DownloadTime: number; // ms
  largestDownload: number; // bytes
  slowestDownload: number; // ms
  networkErrorRate: number; // percentage
}

class DownloadLoggingService {
  private websiteId: string | null = null;
  private eventBuffer: DownloadEvent[] = [];
  private flushInterval = 5000; // 5 seconds
  private maxBufferSize = 50;
  private flushTimer: NodeJS.Timeout | null = null;

  constructor() {
    // Start automatic buffer flushing
    this.startFlushTimer();

    // Flush buffer before page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.flushBuffer();
      });
    }
  }

  setWebsiteId(websiteId: string): void {
    this.websiteId = websiteId;
  }

  getWebsiteId(): string {
    if (!this.websiteId) {
      this.websiteId = import.meta.env.VITE_WEBSITE_ID;
    }

    if (!this.websiteId) {
      throw new Error("Website ID is not set for DownloadLoggingService.");
    }

    return this.websiteId;
  }

  /**
   * Log download event (buffered for performance)
   */
  logEvent(event: Omit<DownloadEvent, "timestamp">): void {
    const fullEvent: DownloadEvent = {
      ...event,
      timestamp: new Date(),
      metadata: {
        ...event.metadata,
        websiteId: this.getWebsiteId(),
      },
    };

    this.eventBuffer.push(fullEvent);

    // Flush buffer if it's getting full
    if (this.eventBuffer.length >= this.maxBufferSize) {
      this.flushBuffer();
    }
  }

  /**
   * Log download start event
   */
  logDownloadStart(
    downloadId: string,
    token: string,
    metadata: {
      productId?: string;
      fileType?: "zip" | "audio" | "other";
      expectedSize?: number;
      contentType?: string;
      userAgent?: string;
      ipAddress?: string;
    },
  ): void {
    this.logEvent({
      downloadId,
      token,
      eventType: "started",
      metadata: {
        ...metadata,
        totalBytes: metadata.expectedSize,
      },
    });
  }

  /**
   * Log download progress event
   */
  logDownloadProgress(
    downloadId: string,
    token: string,
    bytesTransferred: number,
    totalBytes: number,
    duration: number,
  ): void {
    this.logEvent({
      downloadId,
      token,
      eventType: "progress",
      metadata: {
        bytesTransferred,
        totalBytes,
        duration,
      },
    });
  }

  /**
   * Log download completion event
   */
  logDownloadComplete(
    downloadId: string,
    token: string,
    metadata: {
      productId?: string;
      fileType?: "zip" | "audio" | "other";
      fileSize: number;
      duration: number;
      contentType?: string;
      resumeAttempt?: boolean;
    },
  ): void {
    this.logEvent({
      downloadId,
      token,
      eventType: "completed",
      metadata: {
        ...metadata,
        bytesTransferred: metadata.fileSize,
        totalBytes: metadata.fileSize,
      },
    });
  }

  /**
   * Log download failure event
   */
  logDownloadFailure(
    downloadId: string,
    token: string,
    error: {
      code: string;
      message: string;
    },
    metadata: {
      productId?: string;
      fileType?: "zip" | "audio" | "other";
      bytesTransferred?: number;
      totalBytes?: number;
      duration?: number;
      resumeAttempt?: boolean;
      resumePosition?: number;
    },
  ): void {
    this.logEvent({
      downloadId,
      token,
      eventType: "failed",
      metadata: {
        ...metadata,
        errorCode: error.code,
        errorMessage: error.message,
      },
    });
  }

  /**
   * Log download cancellation event
   */
  logDownloadCancellation(
    downloadId: string,
    token: string,
    metadata: {
      productId?: string;
      fileType?: "zip" | "audio" | "other";
      bytesTransferred?: number;
      totalBytes?: number;
      duration?: number;
    },
  ): void {
    this.logEvent({
      downloadId,
      token,
      eventType: "cancelled",
      metadata,
    });
  }

  /**
   * Flush buffered events to database
   */
  private async flushBuffer(): Promise<void> {
    if (this.eventBuffer.length === 0) {
      return;
    }

    const eventsToFlush = [...this.eventBuffer];
    this.eventBuffer = [];

    try {
      // Group events by type for efficient insertion
      const eventsByType = eventsToFlush.reduce(
        (acc, event) => {
          if (!acc[event.eventType]) {
            acc[event.eventType] = [];
          }
          acc[event.eventType].push(event);
          return acc;
        },
        {} as Record<string, DownloadEvent[]>,
      );

      // Insert events into download_events table
      for (const [eventType, events] of Object.entries(eventsByType)) {
        const eventRecords = events.map((event) => ({
          download_id: event.downloadId,
          token: event.token,
          event_type: eventType,
          created_at: event.timestamp.toISOString(),
          metadata: event.metadata,
          website_id: this.getWebsiteId(),
        }));

        const { error } = await supabase
          .from("download_events")
          .insert(eventRecords);

        if (error) {
          console.error(`Failed to log ${eventType} events:`, error);
          // Re-add failed events to buffer for retry
          this.eventBuffer.push(...events);
        }
      }
    } catch (error) {
      console.error("Failed to flush download events:", error);
      // Re-add all events to buffer for retry
      this.eventBuffer.push(...eventsToFlush);
    }
  }

  /**
   * Start automatic buffer flushing timer
   */
  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flushBuffer();
    }, this.flushInterval);
  }

  /**
   * Stop automatic buffer flushing
   */
  stopLogging(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Flush any remaining events
    this.flushBuffer();
  }

  /**
   * Get download analytics for a specific time period
   */
  async getDownloadAnalytics(
    startDate: Date,
    endDate: Date,
    productId?: string,
  ): Promise<DownloadAnalytics> {
    try {
      let query = supabase
        .from("download_events")
        .select(
          `
          event_type,
          metadata,
          created_at
        `,
        )
        .eq("website_id", this.getWebsiteId())
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      if (productId) {
        query = query.eq("metadata->>productId", productId);
      }

      const { data: events, error } = await query;

      if (error) throw error;

      // Process events to calculate analytics
      const analytics: DownloadAnalytics = {
        totalDownloads: 0,
        completedDownloads: 0,
        failedDownloads: 0,
        totalBytesTransferred: 0,
        averageDownloadTime: 0,
        successRate: 0,
        failuresByError: {},
        downloadsByFileType: {},
        resumeAttempts: 0,
      };

      const downloadIds = new Set<string>();
      const downloadTimes: number[] = [];
      let totalDuration = 0;

      events?.forEach((event) => {
        const metadata = event.metadata || {};

        switch (event.event_type) {
          case "started": {
            downloadIds.add(event.download_id);
            analytics.totalDownloads++;

            // Track file type
            const fileType = metadata.fileType || "other";
            analytics.downloadsByFileType[fileType] =
              (analytics.downloadsByFileType[fileType] || 0) + 1;
            break;
          }

          case "completed":
            analytics.completedDownloads++;
            if (metadata.bytesTransferred) {
              analytics.totalBytesTransferred += metadata.bytesTransferred;
            }
            if (metadata.duration) {
              downloadTimes.push(metadata.duration);
              totalDuration += metadata.duration;
            }
            if (metadata.resumeAttempt) {
              analytics.resumeAttempts++;
            }
            break;

          case "failed":
            analytics.failedDownloads++;
            if (metadata.errorCode) {
              analytics.failuresByError[metadata.errorCode] =
                (analytics.failuresByError[metadata.errorCode] || 0) + 1;
            }
            if (metadata.resumeAttempt) {
              analytics.resumeAttempts++;
            }
            break;
        }
      });

      // Calculate derived metrics
      analytics.successRate =
        analytics.totalDownloads > 0
          ? (analytics.completedDownloads / analytics.totalDownloads) * 100
          : 0;

      analytics.averageDownloadTime =
        downloadTimes.length > 0 ? totalDuration / downloadTimes.length : 0;

      return analytics;
    } catch (error) {
      console.error("Failed to get download analytics:", error);
      throw new Error("Failed to retrieve download analytics");
    }
  }

  /**
   * Get performance metrics for downloads
   */
  async getPerformanceMetrics(
    startDate: Date,
    endDate: Date,
  ): Promise<PerformanceMetrics> {
    try {
      const { data: completedDownloads, error } = await supabase
        .from("download_events")
        .select(
          `
          metadata
        `,
        )
        .eq("website_id", this.getWebsiteId())
        .eq("event_type", "completed")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      if (error) throw error;

      const metrics: PerformanceMetrics = {
        averageSpeed: 0,
        p95DownloadTime: 0,
        p99DownloadTime: 0,
        largestDownload: 0,
        slowestDownload: 0,
        networkErrorRate: 0,
      };

      if (!completedDownloads || completedDownloads.length === 0) {
        return metrics;
      }

      const speeds: number[] = [];
      const downloadTimes: number[] = [];
      const fileSizes: number[] = [];

      completedDownloads.forEach((download) => {
        const metadata = download.metadata || {};

        if (metadata.duration && metadata.bytesTransferred) {
          const speedKBps =
            metadata.bytesTransferred / 1024 / (metadata.duration / 1000);
          speeds.push(speedKBps);
          downloadTimes.push(metadata.duration);
          fileSizes.push(metadata.bytesTransferred);
        }
      });

      // Calculate metrics
      if (speeds.length > 0) {
        metrics.averageSpeed =
          speeds.reduce((a, b) => a + b, 0) / speeds.length;
      }

      if (downloadTimes.length > 0) {
        downloadTimes.sort((a, b) => a - b);
        const p95Index = Math.floor(downloadTimes.length * 0.95);
        const p99Index = Math.floor(downloadTimes.length * 0.99);

        metrics.p95DownloadTime = downloadTimes[p95Index] || 0;
        metrics.p99DownloadTime = downloadTimes[p99Index] || 0;
        metrics.slowestDownload = downloadTimes[downloadTimes.length - 1] || 0;
      }

      if (fileSizes.length > 0) {
        metrics.largestDownload = Math.max(...fileSizes);
      }

      // Calculate network error rate
      const { data: failedDownloads } = await supabase
        .from("download_events")
        .select("metadata")
        .eq("website_id", this.getWebsiteId())
        .eq("event_type", "failed")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      const networkErrors =
        failedDownloads?.filter(
          (download) => download.metadata?.errorCode === "NETWORK_ERROR",
        ).length || 0;

      const totalAttempts =
        completedDownloads.length + (failedDownloads?.length || 0);
      metrics.networkErrorRate =
        totalAttempts > 0 ? (networkErrors / totalAttempts) * 100 : 0;

      return metrics;
    } catch (error) {
      console.error("Failed to get performance metrics:", error);
      throw new Error("Failed to retrieve performance metrics");
    }
  }

  /**
   * Get failure rate for a specific time period
   */
  async getFailureRate(
    startDate: Date,
    endDate: Date,
    fileType?: "zip" | "audio" | "other",
  ): Promise<{
    totalAttempts: number;
    failures: number;
    failureRate: number;
    topErrors: Array<{ code: string; count: number; percentage: number }>;
  }> {
    try {
      let query = supabase
        .from("download_events")
        .select(
          `
          event_type,
          metadata
        `,
        )
        .eq("website_id", this.getWebsiteId())
        .in("event_type", ["started", "failed"])
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      const { data: events, error } = await query;

      if (error) throw error;

      let totalAttempts = 0;
      let failures = 0;
      const errorCounts: Record<string, number> = {};

      events?.forEach((event) => {
        const metadata = event.metadata || {};

        // Filter by file type if specified
        if (fileType && metadata.fileType !== fileType) {
          return;
        }

        if (event.event_type === "started") {
          totalAttempts++;
        } else if (event.event_type === "failed") {
          failures++;
          const errorCode = metadata.errorCode || "UNKNOWN_ERROR";
          errorCounts[errorCode] = (errorCounts[errorCode] || 0) + 1;
        }
      });

      const failureRate =
        totalAttempts > 0 ? (failures / totalAttempts) * 100 : 0;

      // Sort errors by count and calculate percentages
      const topErrors = Object.entries(errorCounts)
        .map(([code, count]) => ({
          code,
          count,
          percentage: failures > 0 ? (count / failures) * 100 : 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Top 10 errors

      return {
        totalAttempts,
        failures,
        failureRate,
        topErrors,
      };
    } catch (error) {
      console.error("Failed to get failure rate:", error);
      throw new Error("Failed to retrieve failure rate");
    }
  }
}

export const downloadLoggingService = new DownloadLoggingService();
export type { DownloadEvent, DownloadAnalytics, PerformanceMetrics };
